/**
 * Onglet « Autres » du dossier — fourre-tout (courriers, notes, divers).
 *
 * PAS analysé par l'IA. Pour faire entrer un document dans le RAG/IA, il
 * faut le **promouvoir** vers Pièces clients (bouton dédié) — cela crée
 * un `PieceClient` et supprime le `OtherDocument`.
 *
 * Préparation au branchement Google Drive : quand on importera tout un
 * dossier depuis Drive, ce qui ne matche aucune catégorie connue
 * (répressif / pièces clients / conclusions / pièces adverses) finira
 * automatiquement ici.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconEye, IconTrash, IconUpload, IconArrowsExchange, IconFileText } from '@tabler/icons-react';
import Btn from './ui/Btn';
import PushToDriveButton from './PushToDriveButton';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';
import { useAuthStore } from '../stores/auth';

type OtherDocument = {
  id: string;
  dossier_id: string;
  filename_original: string;
  file_size_bytes: number;
  kind: string | null;
  title: string | null;
  notes: string | null;
  uploaded_by: string | null;
  uploaded_at: string;
  is_converted: boolean;
  conversion_status: string | null;
};

function isPdfViewable(d: OtherDocument): boolean {
  return d.is_converted || d.filename_original.toLowerCase().endsWith('.pdf');
}

const KINDS = ['courrier', 'note', 'facture', 'convention_honoraires', 'autre'] as const;
type Kind = (typeof KINDS)[number];

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
}

export default function OtherDocumentsTab({ dossierId }: { dossierId: string }) {
  const [docs, setDocs] = useState<OtherDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const accessToken = useAuthStore((s) => s.accessToken);

  // Formulaire upload
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadKind, setUploadKind] = useState<Kind>('courrier');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api.get<OtherDocument[]>(`/dossiers/${dossierId}/other-documents`);
      setDocs(rows);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setLoading(false);
    }
  }, [dossierId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error('Sélectionne un fichier.');
      return;
    }
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append('file', uploadFile);
      form.append('kind', uploadKind);
      if (uploadTitle.trim()) form.append('title', uploadTitle.trim());
      if (uploadNotes.trim()) form.append('notes', uploadNotes.trim());
      await api.postForm<OtherDocument>(`/dossiers/${dossierId}/other-documents`, form);
      setUploadFile(null);
      setUploadTitle('');
      setUploadNotes('');
      setUploadKind('courrier');
      setUploadOpen(false);
      await reload();
      toast.success('Document ajouté');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setUploadBusy(false);
    }
  };

  const onDelete = async (doc: OtherDocument) => {
    if (!confirm(`Supprimer définitivement « ${doc.title || doc.filename_original} » ?`)) return;
    try {
      await api.delete<void>(`/other-documents/${doc.id}`);
      await reload();
      toast.success('Document supprimé');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const onPromote = async (doc: OtherDocument) => {
    const titre = prompt(
      "Titre pour la Pièce client (l'IA verra cette pièce dans le RAG) ?",
      doc.title || doc.filename_original
    );
    if (titre === null) return;
    try {
      await api.post<{ piece_client_id: string }>(`/other-documents/${doc.id}/promote`, {
        titre: titre || undefined,
      });
      await reload();
      toast.success("Promu vers Pièces clients (visible désormais par l'IA).");
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const onDownload = (doc: OtherDocument) => {
    if (!accessToken) return;
    const url = `/api/v1/other-documents/${doc.id}/stream`;
    // Téléchargement via fetch + Bearer token
    fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        a.download = doc.filename_original;
        a.click();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error('Téléchargement échoué'));
  };

  return (
    <div className="space-y-5 px-6 py-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-[20px] font-medium text-text-primary">Autres documents</h1>
          <p className="mt-1 text-[13px] text-text-secondary">
            Courriers, notes, factures, conventions, divers. <strong>Pas analysé par l'IA</strong> —
            promouvoir vers Pièces clients pour qu'un document entre dans le RAG.
          </p>
        </div>
        <Btn variant="primary" onClick={() => setUploadOpen((v) => !v)}>
          <IconUpload size={16} className="-ml-0.5 mr-1.5" />
          {uploadOpen ? 'Annuler' : 'Ajouter un document'}
        </Btn>
      </header>

      {uploadOpen && (
        <form
          onSubmit={onUpload}
          className="space-y-3 rounded-lg border-[1.5px] border-dashed border-accent bg-accent-lighter p-4"
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <label className="block">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
                Type
              </span>
              <select
                value={uploadKind}
                onChange={(e) => setUploadKind(e.target.value as Kind)}
                className="mt-1.5 w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm focus:border-accent focus:outline-none"
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </label>
            <label className="block md:col-span-2">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
                Titre (optionnel)
              </span>
              <input
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder="Par défaut : nom du fichier"
                className="mt-1.5 w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
            </label>
          </div>
          <label className="block">
            <span className="block text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
              Fichier
            </span>
            <input
              type="file"
              onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
              className="mt-1.5 block w-full text-sm"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
              Notes (optionnel)
            </span>
            <textarea
              value={uploadNotes}
              onChange={(e) => setUploadNotes(e.target.value)}
              rows={2}
              className="mt-1.5 w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm focus:border-accent focus:outline-none"
            />
          </label>
          <div className="flex justify-end">
            <Btn variant="primary" disabled={uploadBusy || !uploadFile}>
              {uploadBusy ? 'Envoi…' : 'Ajouter'}
            </Btn>
          </div>
        </form>
      )}

      {error && (
        <p className="rounded-md border border-cat-def/40 bg-cat-def-pastel px-3 py-2 text-sm text-cat-def">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-[13px] text-text-secondary">Chargement…</p>
      ) : docs.length === 0 ? (
        <p className="rounded-md border border-dashed border-rule p-6 text-center text-[13px] text-text-secondary">
          Aucun document dans cette catégorie. Ajoute des courriers, notes ou conventions ici.
        </p>
      ) : (
        <ul className="space-y-2">
          {docs.map((d) => (
            <li
              key={d.id}
              className="flex items-center gap-3 rounded-md border border-rule bg-paper-elevated p-3"
            >
              <IconFileText size={20} stroke={1.6} className="shrink-0 text-text-secondary" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-[13px] text-text-primary">
                  {d.title || d.filename_original}
                </p>
                <p className="mt-0.5 truncate text-[11px] text-text-tertiary">
                  {d.kind && (
                    <span className="rounded-pill bg-paper-subtle px-1.5 py-[1px] mr-1.5 font-mono uppercase">
                      {d.kind}
                    </span>
                  )}
                  {d.is_converted && (
                    <span
                      title="Le fichier original a été converti en PDF à l'import (.msg/.eml/...). L'original n'est plus stocké."
                      className="rounded-pill bg-accent-lighter px-1.5 py-[1px] mr-1.5 font-mono uppercase text-accent-text"
                    >
                      PDF généré
                    </span>
                  )}
                  {d.filename_original} · {formatBytes(d.file_size_bytes)} ·{' '}
                  {new Date(d.uploaded_at).toLocaleDateString('fr-BE')}
                </p>
                {d.notes && (
                  <p className="mt-1 text-[11px] italic text-text-secondary">{d.notes}</p>
                )}
              </div>
              {isPdfViewable(d) && (
                <Link
                  to={`/dossiers/${dossierId}/other-documents/${d.id}`}
                  title="Ouvrir dans le viewer (lecture seule)"
                  className="inline-flex items-center gap-1 rounded-md border border-rule-strong bg-paper-elevated px-2 py-1 text-[11px] text-text-primary hover:bg-paper-subtle"
                >
                  <IconEye size={13} stroke={1.6} />
                  Ouvrir
                </Link>
              )}
              <button
                type="button"
                onClick={() => onDownload(d)}
                className="rounded-md border border-rule bg-paper-card px-2 py-1 text-[11px] hover:bg-paper-subtle"
              >
                Télécharger
              </button>
              <button
                type="button"
                onClick={() => onPromote(d)}
                title="Promouvoir vers Pièces clients (entre dans l'IA/RAG)"
                className="rounded-md border border-accent/40 bg-accent-lighter px-2 py-1 text-[11px] text-accent-text hover:bg-accent/10"
              >
                <IconArrowsExchange size={12} className="-mt-0.5 mr-0.5 inline-block" />
                Promouvoir
              </button>
              <PushToDriveButton
                dossierId={dossierId}
                kind="other_document"
                pieceId={d.id}
                pieceTitle={d.title || d.filename_original}
                variant="icon"
              />
              <button
                type="button"
                onClick={() => onDelete(d)}
                title="Supprimer"
                className="rounded-md border border-cat-def/40 bg-cat-def-pastel px-2 py-1 text-[11px] text-cat-def hover:bg-cat-def/10"
              >
                <IconTrash size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
